import { useEffect, useState } from "react";
import { Plus, X, Key, Pencil, Prohibit, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { inputCls, labelCls } from "./shared";

const ROLES = { admin: "Amministratore", manager: "Manager", sala: "Sala" };

export default function UtentiSection() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [showUser, setShowUser] = useState(false);
  const [userForm, setUserForm] = useState({ name: "", email: "", password: "", role: "sala" });
  const [resetUser, setResetUser] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addUser = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", userForm);
      toast.success("Utente creato");
      setShowUser(false);
      setUserForm({ name: "", email: "", password: "", role: "sala" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const toggleActive = async (u) => {
    const target = u.active === false;
    try {
      await api.patch(`/users/${u.id}`, { active: target });
      toast.success(target ? `Utente "${u.name}" riattivato` : `Utente "${u.name}" disattivato`);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) return toast.error("Password troppo corta (minimo 6 caratteri)");
    setSaving(true);
    try {
      await api.patch(`/users/${resetUser.id}`, { password: newPwd });
      toast.success(`Password di "${resetUser.name}" aggiornata`);
      setResetUser(null);
      setNewPwd("");
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const doEditEmail = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, { email: editEmail });
      toast.success(`Email di "${editUser.name}" aggiornata`);
      setEditUser(null);
      setEditEmail("");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="setsection-utenti">
      <section className="bg-white border border-zinc-200 rounded-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-200">
          <div>
            <h2 className="font-display font-semibold text-zinc-900">Utenti e ruoli</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Admin: tutto. Manager: operatività e marketing. Sala: servizio e prenotazioni.</p>
          </div>
          <button onClick={() => setShowUser(true)} data-testid="btn-nuovo-utente"
            className="h-9 px-3 rounded-md bg-zinc-900 text-white text-xs font-semibold flex items-center gap-1.5">
            <Plus size={14} weight="bold" /> Utente
          </button>
        </div>
        <div className="divide-y divide-zinc-100" data-testid="users-list">
          {users.map((u) => {
            const inactive = u.active === false;
            const isSelf = u.id === me?.id;
            return (
              <div key={u.id} className={`flex items-center gap-3 px-5 py-3 ${inactive ? "opacity-60" : ""}`}
                data-testid={`user-row-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                    {u.name}
                    {inactive && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700"
                        data-testid={`user-inactive-${u.id}`}>DISATTIVATO</span>
                    )}
                  </div>
                  <div className="text-xs text-zinc-500">{u.email}</div>
                </div>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-700 border-zinc-300">
                  {ROLES[u.role] || u.role}
                </span>
                <button onClick={() => { setEditUser(u); setEditEmail(u.email); }} data-testid={`user-edit-${u.id}`}
                  title="Modifica email di accesso"
                  className="p-2 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors">
                  <Pencil size={16} weight="bold" />
                </button>
                <button onClick={() => { setResetUser(u); setNewPwd(""); }} data-testid={`user-reset-${u.id}`}
                  title="Reimposta password"
                  className="p-2 rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 transition-colors">
                  <Key size={16} weight="bold" />
                </button>
                {!isSelf && (
                  <button onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}
                    title={inactive ? "Riattiva utente" : "Disattiva utente"}
                    className={`p-2 rounded-md transition-colors ${
                      inactive ? "text-emerald-600 hover:bg-emerald-50" : "text-red-500 hover:bg-red-50"}`}>
                    {inactive ? <ArrowCounterClockwise size={16} weight="bold" /> : <Prohibit size={16} weight="bold" />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {showUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setShowUser(false)}>
          <form onSubmit={addUser} className="bg-white w-full sm:max-w-md rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="user-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Nuovo utente</h3>
              <button type="button" onClick={() => setShowUser(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <input required value={userForm.name} onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
              placeholder="Nome" data-testid="user-name" className={inputCls} />
            <input required type="email" value={userForm.email} onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
              placeholder="Email" data-testid="user-email" className={inputCls} />
            <input required type="password" value={userForm.password} onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
              placeholder="Password" data-testid="user-password" className={inputCls} />
            <select value={userForm.role} onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
              data-testid="user-role" className={inputCls}>
              {Object.entries(ROLES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <button className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold" data-testid="user-submit">Crea utente</button>
          </form>
        </div>
      )}

      {editUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setEditUser(null)}>
          <form onSubmit={doEditEmail} className="bg-white w-full sm:max-w-sm rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="edit-email-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Modifica email di accesso</h3>
              <button type="button" onClick={() => setEditUser(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-600">Nuova email per <b>{editUser.name}</b>. Da subito il login si fa con la nuova email (la password resta invariata).</p>
            <input required type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)}
              placeholder="Nuova email" data-testid="edit-email-input" className={inputCls} />
            <button disabled={saving}
              className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="edit-email-submit">{saving ? "Salvataggio..." : "Aggiorna email"}</button>
          </form>
        </div>
      )}

      {resetUser && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => setResetUser(null)}>
          <form onSubmit={doReset} className="bg-white w-full sm:max-w-sm rounded-t-xl sm:rounded-lg p-5 space-y-3"
            onClick={(e) => e.stopPropagation()} data-testid="reset-dialog">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">Reimposta password</h3>
              <button type="button" onClick={() => setResetUser(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <p className="text-sm text-zinc-600">Nuova password per <b>{resetUser.name}</b> ({resetUser.email})</p>
            <input required type="password" minLength={6} value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              placeholder="Nuova password (min. 6 caratteri)" data-testid="reset-password-input" className={inputCls} />
            <button disabled={saving}
              className="w-full h-12 rounded-md bg-zinc-900 text-white text-sm font-semibold disabled:opacity-50"
              data-testid="reset-submit">{saving ? "Salvataggio..." : "Aggiorna password"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
